import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { SelectButton } from 'primereact/selectbutton';
import { AppointmentsDTO } from '../../../interface/interface';
import { getAgendaSchedule } from '../services/AgendaService';
import { AgendaSchedule } from '../types/agenda.types';
import './AvailabilityPanel.scss';

type AvailabilityFilter = 'available' | 'occupied' | 'all';

const FILTER_OPTIONS: { label: string; value: AvailabilityFilter }[] = [
  { label: 'Libres', value: 'available' },
  { label: 'Ocupados', value: 'occupied' },
  { label: 'Todos', value: 'all' },
];

interface AvailabilityPanelProps {
  selectedAgendas: AgendaSchedule[];
  buildingId?: number | null;
  selectedDate: Date;
  refreshKey?: number;
  onSelectSlot: (time: string, agenda: AgendaSchedule) => void;
  selectedProfessionalId?: number | null;
}

function normalizeTime(time?: string | null): string {
  return time ? time.substring(0, 5) : '';
}

export default function AvailabilityPanel({
  selectedAgendas,
  buildingId,
  selectedDate,
  refreshKey = 0,
  onSelectSlot,
  selectedProfessionalId,
}: AvailabilityPanelProps) {
  const [slots, setSlots] = useState<AppointmentsDTO[]>([]);
  const [filter, setFilter] = useState<AvailabilityFilter>('available');
  const [loading, setLoading] = useState(false);

  const dateKey = format(selectedDate, 'yyyy-MM-dd');
  const scheduleTemplateIds = useMemo(
    () => Array.from(new Set(selectedAgendas.flatMap((agenda) => agenda.templateIds))),
    [selectedAgendas],
  );
  const professionalIds = useMemo(
    () => Array.from(new Set(
      selectedAgendas
        .filter((agenda) => agenda.agendaType === 'professional' && agenda.professionalId > 0)
        .map((agenda) => agenda.professionalId),
    )),
    [selectedAgendas],
  );
  const specialityIds = useMemo(
    () => Array.from(new Set(
      selectedAgendas
        .filter((agenda) => agenda.agendaType === 'service')
        .flatMap((agenda) => agenda.specialityIds ?? []),
    )),
    [selectedAgendas],
  );
  const scheduleTemplateIdsKey = scheduleTemplateIds.join(',');
  const professionalIdsKey = professionalIds.join(',');
  const specialityIdsKey = specialityIds.join(',');

  const hasAgendas = scheduleTemplateIds.length > 0;
  const hasProfessionalOnly = !hasAgendas && !!selectedProfessionalId;
  const canLoad = hasAgendas || hasProfessionalOnly;

  useEffect(() => {
    let active = true;
    if (!canLoad) {
      setSlots([]);
      setLoading(false);
      return () => { active = false; };
    }

    const profIds = hasAgendas
      ? (professionalIds.length > 0 ? professionalIds : null)
      : [selectedProfessionalId!];

    setLoading(true);
    getAgendaSchedule(
      dateKey,
      profIds,
      specialityIds.length > 0 ? specialityIds : null,
      0,
      0,
      hasAgendas ? scheduleTemplateIds : null,
      false,
      null,
      buildingId,
    )
      .then((response) => {
        if (active) setSlots(response.data.filter((slot) => !!slot.initialhour));
      })
      .catch(() => {
        if (active) setSlots([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [dateKey, professionalIdsKey, specialityIdsKey, scheduleTemplateIdsKey, refreshKey, buildingId, selectedProfessionalId]);

  const visibleSlots = useMemo(() => {
    if (filter === 'available') return slots.filter((slot) => slot.isavailable);
    if (filter === 'occupied') return slots.filter((slot) => !slot.isavailable);
    return slots;
  }, [filter, slots]);

  const findAgendaForSlot = (slot: AppointmentsDTO): AgendaSchedule | null => {
    const matchingAgenda = selectedAgendas.find((agenda) =>
      agenda.templateIds.includes(slot.medicalscheduletemplate),
    );
    return matchingAgenda ?? (selectedAgendas.length === 1 ? selectedAgendas[0] : null);
  };

  const slotTitle = (isAvailable: boolean, hasAgenda: boolean, time: string) => {
    if (!isAvailable) return 'Horario ocupado';
    if (!hasAgenda) return 'Seleccioná una agenda para agendar en este horario';
    return `Agendar turno a las ${time}`;
  };

  return (
    <section className="nv-card availability-panel" aria-labelledby="availability-panel-title">
      <h3 id="availability-panel-title" className="nv-section-title">Disponibilidad</h3>

      <SelectButton
        value={filter}
        options={FILTER_OPTIONS}
        onChange={(e) => e.value && setFilter(e.value)}
        unselectable={false}
        aria-label="Filtro de disponibilidad"
      />

      {loading && (
        <div className="nv-loading availability-panel__message" role="status">
          <i className="pi pi-spinner pi-spin" aria-hidden /> Cargando horarios…
        </div>
      )}
      {!loading && visibleSlots.length === 0 && (
        <p className="nv-muted availability-panel__message">No hay horarios para mostrar.</p>
      )}
      {!loading && visibleSlots.length > 0 && (
        <div className="availability-panel__slots">
          {visibleSlots.map((slot, index) => {
            const time = normalizeTime(slot.initialhour);
            const agenda = findAgendaForSlot(slot);
            const isAvailable = slot.isavailable;
            return (
              <button
                key={`${slot.medicalschedulesetup}-${time}-${index}`}
                type="button"
                className={`availability-slot${isAvailable ? '' : ' availability-slot--occupied'}`}
                disabled={!isAvailable || !agenda}
                onClick={() => agenda && onSelectSlot(time, agenda)}
                title={slotTitle(isAvailable, !!agenda, time)}
              >
                {time}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
